import { Box, Typography, keyframes, alpha, useTheme } from '@mui/material';
import { LuSparkles } from 'react-icons/lu';
import AppIcon from '../../../components/icons/app-icon';

interface AnimatedReleaseBadgeProps {
  label: string;
  /** Render on top of a solid primary surface (e.g. the modal header). */
  onPrimary?: boolean;
}

/**
 * A celebratory, gently animated badge for major releases. A twinkling sparkle
 * paired with a soft pulsing ring draws the eye without the tacky bounce — and
 * it stands down entirely for users who prefer reduced motion.
 */
const AnimatedReleaseBadge = ({
  label,
  onPrimary = false,
}: AnimatedReleaseBadgeProps) => {
  const theme = useTheme();
  const base = onPrimary
    ? theme.palette.primary.contrastText
    : theme.palette.primary.main;

  const pulse = keyframes`
    0% { box-shadow: 0 0 0 0 ${alpha(base, 0.45)}; }
    70% { box-shadow: 0 0 0 6px ${alpha(base, 0)}; }
    100% { box-shadow: 0 0 0 0 ${alpha(base, 0)}; }
  `;

  const twinkle = keyframes`
    0%, 100% { opacity: 1; transform: scale(1) rotate(0deg); }
    50% { opacity: 0.55; transform: scale(1.2) rotate(14deg); }
  `;

  return (
    <Box
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.5,
        px: 1,
        py: '3px',
        borderRadius: 999,
        color: base,
        backgroundColor: alpha(base, onPrimary ? 0.2 : 0.14),
        border: `1px solid ${alpha(base, onPrimary ? 0.45 : 0.4)}`,
        animation: `${pulse} 2.4s ease-out infinite`,
        '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
      }}
    >
      <AppIcon
        icon={LuSparkles}
        size={13}
        color={base}
        sx={{
          animation: `${twinkle} 2.4s ease-in-out infinite`,
          '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
        }}
      />
      <Typography
        component='span'
        sx={{
          fontSize: 10.5,
          fontWeight: 800,
          letterSpacing: 0.4,
          lineHeight: 1,
          textTransform: 'uppercase',
        }}
      >
        {label}
      </Typography>
    </Box>
  );
};

export default AnimatedReleaseBadge;
